<template>
  <div>
    <b-alert variant="success" :show="showAlert_success" @dismissed="showAlert_success = false">
      {{ alert_success_message }}
    </b-alert>

    <b-alert variant="danger" :show="showAlert_error" @dismissed="showAlert_error = false">
      {{ alert_error_message }}
    </b-alert>

    <b-container fluid>
      <b-breadcrumb class="mt-3 mb-3" :items="breadcrumb_items" />

      <b-card class="mb-1">
        <!-- Template Name -->
        <b-form-group id="grp_txt_template_name" label="Template Name:" label-for="txt_template_name" label-size="sm">
          <b-form-input
            id="txt_template_name"
            type="text"
            required
            placeholder="Enter template name"
            v-model="address_obj.template_name"
          ></b-form-input>
        </b-form-group>

        <!-- Name -->
        <b-form-group id="grp_txt_name" label="Name:" label-for="txt_name" label-size="sm">
          <b-form-input
            id="txt_name"
            type="text"
            required
            placeholder="Enter name"
            v-model="address_obj.name"
          ></b-form-input>
        </b-form-group>

        <!-- Phone -->
        <b-form-group id="grp_txt_phone" label="Phone:" label-for="txt_phone" label-size="sm">
          <b-form-input
            id="txt_phone"
            type="text"
            required
            placeholder="Enter phone"
            v-model="address_obj.phone"
          ></b-form-input>
        </b-form-group>

        <!-- District Dropdown -->
        <b-form-group id="grp_select_district" label="District:" label-for="select_district" label-size="sm">
          <b-form-select
            id="select_district"
            v-model="address_obj.district"
            :options="district_options"
            required
          ></b-form-select>
        </b-form-group>

        <!-- Address -->
        <b-form-group id="grp_txt_address" label="Address:" label-for="txt_address" label-size="sm">
          <b-form-textarea
            id="txt_address"
            required
            placeholder="Enter full address"
            v-model="address_obj.address"
            rows="3"
          ></b-form-textarea>
        </b-form-group>

        <!-- Remark -->
        <b-form-group id="grp_txt_remark" label="Remark:" label-for="txt_remark" label-size="sm">
          <b-form-textarea
            id="txt_remark"
            placeholder="Enter remark"
            v-model="address_obj.remark"
            rows="2"
          ></b-form-textarea>
        </b-form-group>

        <!-- Submit Button -->
        <b-button class="float-right" type="submit" variant="primary" @click="saveAddress">Save</b-button>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";
import global_function from "../mixins/global_function";
import { validationMixin } from "vuelidate";
import apiService from "../services/ApiService";

export default {
  name: "OrderAddressEdit",
  inject: ["API_HOST", "TABLE_LIMIT", "PAGING_LIMIT"],
  mixins: [validationMixin, global_function, apiService],
  data() {
    return {
      breadcrumb_items: [
        {
          text: "Address Template",
          to: { name: "address_template" },
        },
        {
          text: "Edit",
          active: true,
        },
      ],
      address_obj: {
        template_name: "",
        name: "",
        phone: "",
        district: "",
        address: "",
        remark: "", // Added remark field
      },
      district_options: [], // Dropdown options for districts
      showAlert_success: false,
      alert_success_message: "",
      showAlert_error: false,
      alert_error_message: "",
    };
  },
  async created() {
    await this.loadDistrictOptions(); // Load districts when the component is created

    // Check if we're editing an existing address template
    if (this.$route.params.address_template_id) {
      await this.loadAddressTemplate(this.$route.params.address_template_id);
    }
  },
  methods: {
    async loadDistrictOptions() {
      try {
        // Fetch districts and populate the dropdown
        let districts_response = await this.getDistricts(this.$store.state.user_obj);
        for (let i in districts_response.districts) {
          this.district_options.push({
            value: districts_response.districts[i].district,
            text: districts_response.districts[i].district,
          });
        }
      } catch (error) {
        console.error("Error loading districts:", error);
      }
    },

    async loadAddressTemplate(template_id) {
      try {
        // Fetch the address template details from the correct PHP API
        let response = await this.$http({
          method: "POST", // Update to POST if that's how the data is fetched
          baseURL: `${this.API_HOST}`,
          url: "address_template_get.php", // Correct PHP script to fetch data
          data: {
            template_id: template_id, // Send template ID to fetch details
          },
        });

        if (response.data.status === "success" && response.data.templates.length > 0) {
          this.address_obj = response.data.templates[0]; // Pre-fill the form with existing data
        } else {
          this.alert_error_message = "Failed to load address template data.";
          this.showAlert_error = true;
        }
      } catch (error) {
        this.alert_error_message = "Error fetching address template details.";
        this.showAlert_error = true;
      }
    },

    async saveAddress() {
      // Validation
      if (this.address_obj.template_name.trim() === "") {
        this.alert_error_message = `Template Name is required`;
        this.showAlert_error = true;
        setTimeout(() => {
          this.showAlert_error = false;
        }, 3000);
        return;
      }

      if (this.address_obj.name.trim() === "") {
        this.alert_error_message = `Name is required`;
        this.showAlert_error = true;
        setTimeout(() => {
          this.showAlert_error = false;
        }, 3000);
        return;
      }

      if (this.address_obj.phone.trim() === "") {
        this.alert_error_message = `Phone is required`;
        this.showAlert_error = true;
        setTimeout(() => {
          this.showAlert_error = false;
        }, 3000);
        return;
      }

      if (this.address_obj.district.trim() === "") {
        this.alert_error_message = `District is required`;
        this.showAlert_error = true;
        setTimeout(() => {
          this.showAlert_error = false;
        }, 3000);
        return;
      }

      if (this.address_obj.address.trim() === "") {
        this.alert_error_message = `Address is required`;
        this.showAlert_error = true;
        setTimeout(() => {
          this.showAlert_error = false;
        }, 3000);
        return;
      }

      // Construct the request payload with the required fields
      const payload = {
        template_name: this.address_obj.template_name,
        name: this.address_obj.name,
        phone: this.address_obj.phone,
        district: this.address_obj.district,
        address: this.address_obj.address,
        remark: this.address_obj.remark, // Include remark in the payload
        updated_by: this.$store.state.user_obj.user_id,
        client_id: this.$store.state.user_obj.client_id,
      };

      // Check if we're updating an existing address template
      if (this.$route.params.address_template_id) {
        let response = await this.$http({
          method: "POST",
          baseURL: `${this.API_HOST}`,
          url: "address_template_edit.php", // Endpoint for updating
          data: {
            address_template_id: this.$route.params.address_template_id,
            ...payload, // Spread the payload
          },
        });

        if (response.data.status === "success") {
          this.alert_success_message = `Address Updated Successfully.`;
          this.showAlert_success = true;
          setTimeout(() => {
            this.showAlert_success = false;
          }, 3000);
          this.$router.go(-1); // Go back to the previous page
        } else {
          alert(response.data.error_msg);
        }
      }
    },
  },
};
</script>

<style>
th,
td {
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/AddressTemplateEdit.vue